import * as React from 'react';
import { IValue as ImportedValue, OffsiteCampaignEditorProvider } from './OffsiteCampaignEditorProvider';
import { ActionTypes, OffsiteCampaignEditorReducer } from './OffsiteCampaignEditorReducer';
import { Site } from 'sections/client-management/entities';
import { IOffsiteCampaign, IVisitorMessageType } from '../../entities/offsiteCampaign';
import { ITemplate } from 'entities/template/template';
import { ISubjectLine } from 'entities/subjectLine/subjectLine';
export type IValue = ImportedValue;

export interface IAction {
  type: ActionTypes;
  payload?: any;
}

export type Dispatch = (action: IAction) => void;

export interface IState {
  campaign?: IOffsiteCampaign;
  originalCampaign?: IOffsiteCampaign;
  templates?: ITemplate[];
  unsubscribeTemplates?: ITemplate[];
  site?: Site;
  subjectLines?: ISubjectLine[];
  hasUnsavedCampaignChange?: boolean;
  unsavedSegmentChanges?: string[];
  hasMissingFields?: boolean;
  isInActiveSplitTest?: boolean;
  hasOrderChange?: boolean;
  unsubscribeSet: boolean;
  activeChannels?: IVisitorMessageType[];
}

export const defaultContextValues: IState = {
  campaign: {
    state: 'TESTING',
    siteId: '',
    type: 'cartAbandonment',
    segments: [],
    suppressTimeWindows: [],
  },
  originalCampaign: {
    state: 'TESTING',
    siteId: '',
    type: 'cartAbandonment',
    segments: [],
    suppressTimeWindows: [],
  },
  site: {},
  templates: [],
  subjectLines: [],
  hasUnsavedCampaignChange: false,
  unsavedSegmentChanges: [],
  hasMissingFields: false,
  isInActiveSplitTest: false,
  unsubscribeSet: false,
  activeChannels: [],
};

export const OffsiteCampaignEditorStateContext = React.createContext<IState | undefined>(undefined);
export const OffsiteCampaignEditorDispatchContext = React.createContext<Dispatch | undefined>(undefined);

function useOffsiteCampaignEditorState() {
  const context = React.useContext(OffsiteCampaignEditorStateContext);
  if (context === undefined) {
    throw new Error('useOffsiteCampaignEditorState must be used within a OffsiteCampaignEditorProvider');
  }
  return context;
}

function useOffsiteCampaignEditorDispatch() {
  const context = React.useContext(OffsiteCampaignEditorDispatchContext);
  if (context === undefined) {
    throw new Error('useOffsiteCampaignEditorDispatch must be used within a OffsiteCampaignEditorProvider');
  }
  return context;
}

function useDoesSplitTestExist(): boolean {
  const { campaign } = useOffsiteCampaignEditorState();
  return campaign.segments.findIndex(segment => segment.percentageSplits.length > 1) !== -1;
}

const useHasSplitTestEnded = (): boolean => {
  const { campaign } = useOffsiteCampaignEditorState();

  const splitTestExists = campaign.segments.some(s => s.percentageSplits.length > 1);
  if (!splitTestExists) return false;
  const { splitTestEndDate } = campaign;

  return splitTestEndDate === 0 ? false : Date.now() - splitTestEndDate >= 0;
};
const useHasOriginalSplitTestEnded = (): boolean => {
  const { originalCampaign: campaign } = useOffsiteCampaignEditorState();

  const splitTestExists = campaign.segments.some(s => s.percentageSplits.length > 1);
  if (!splitTestExists) return false;
  const { splitTestEndDate } = campaign;

  return splitTestEndDate === 0 ? false : Date.now() - splitTestEndDate >= 0;
};

const useHasSplitTestStarted = (): boolean => {
  const { campaign } = useOffsiteCampaignEditorState();
  const splitTestExists = campaign.segments.some(s => s.percentageSplits.length > 1);
  if (!splitTestExists) return false;
  const { splitTestStartDate } = campaign;
  return splitTestStartDate === 0 || !splitTestStartDate ? true : Date.now() > splitTestStartDate;
};

export {
  OffsiteCampaignEditorProvider,
  OffsiteCampaignEditorReducer,
  useOffsiteCampaignEditorState,
  useOffsiteCampaignEditorDispatch,
  useDoesSplitTestExist,
  useHasSplitTestEnded,
  useHasSplitTestStarted,
  useHasOriginalSplitTestEnded,
};
